import { Analytics } from '@cobliteam/analytics';
import { parseVehicleSize, type Vehicle, type VehicleSize } from '@cobliteam/common-types';
import useFuscaVehicle from '@cobliteam/hooks/src/useEntity/useFuscaVehicles';
import useVehicle from '@cobliteam/hooks/src/useEntity/useVehicle';
import { useTranslation } from '@cobliteam/i18n';
import Slider from '@cobliteam/ui/components/Slider';
import Tag from '@cobliteam/ui/components/Tag';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, useForm } from '@cobliteam/ui/form';
import Input from '@cobliteam/ui/form/Input';
import Select, { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@cobliteam/ui/form/Select';
import Icon from '@cobliteam/ui/icons';
import { DialogSetupGuideShell } from '@cobliteam/ui-business/components/dialogs/DialogSetupGuideShell/DialogSetupGuideShell';
import { formatLastValidPlate, ValidationResult, validateLicensePlate } from '@cobliteam/ui-business/utils/licensePlateValidator';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { z } from 'zod';
import type { SetupGuideEntityFormProps } from '../entityModalConfig';
import SetupGuideFormRow from '../SetupGuideFormRow';
import { rowsForRetry, runSequentialSaves } from './sequentialSubmit';
import { DEFAULT_KMH, MAX_SPEED_LIMIT, PORTE_ORDER } from './speedDefaults';

/** Rows shown in the modal; only the first is required. */
const ROWS = 2;

/**
 * Minimal "register vehicles" modal — plate + porte + allowed speed per row. Brand/model/year live on
 * the vehicles page (the "Configurações avançadas" shortcut): their catalog needs three chained
 * requests per row, too heavy for this quick-setup modal.
 */
const VehiclesForm = ({ fleetId, item, onClose, onSuccess, onAdvancedSettings }: SetupGuideEntityFormProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const { data: vehicles } = useVehicle().useVehicles({});
  const { mutateAsync: createVehicle, isPending } = useFuscaVehicle().createNewVehicle;

  const schema = useMemo(() => {
    const existingPlates = new Set((vehicles ?? []).map((vehicle) => formatLastValidPlate(vehicle.licensePlate ?? '')).filter(Boolean));
    return z.object({
      rows: z.array(z.object({ licensePlate: z.string(), size: z.string(), speedLimitInKmh: z.number() })).superRefine((rows, ctx) => {
        rows.forEach((row, index) => {
          const plate = row.licensePlate.trim();
          if (!plate) {
            // Only the first row is mandatory; later rows may stay empty.
            if (index === 0) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'licensePlate'], message: t('entity_modal.vehicles.validation.plate_required') });
            return;
          }
          // Same permissive rule as the VehicleDrawer: any alphanumeric plate is accepted
          // (out-of-standard plates only warn there, they don't block).
          if (validateLicensePlate(plate) !== ValidationResult.VALID) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'licensePlate'], message: t('entity_modal.vehicles.validation.plate_invalid') });
            return;
          }
          const normalized = formatLastValidPlate(plate);
          const repeatedOnFleet = existingPlates.has(normalized);
          const repeatedOnRows = rows.some((other, otherIndex) => otherIndex < index && formatLastValidPlate(other.licensePlate.trim()) === normalized);
          if (repeatedOnFleet || repeatedOnRows) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'licensePlate'], message: t('entity_modal.vehicles.validation.plate_duplicated') });
          }
          if (!row.size) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'size'], message: t('entity_modal.vehicles.validation.size_required') });
          }
        });
      }),
    });
  }, [vehicles, t]);

  type FormValues = z.infer<typeof schema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { rows: Array.from({ length: ROWS }, () => ({ licensePlate: '', size: '', speedLimitInKmh: 0 })) },
  });

  const handleSubmit = async ({ rows }: FormValues) => {
    // Original indexes travel along so a partial failure can clear exactly the rows that saved.
    const filled = rows.map((row, rowIndex) => ({ row, rowIndex })).filter(({ row }) => row.licensePlate.trim());
    // Sequential creates; a failed row doesn't stop the following ones and the fusca hook toasts failures.
    const { succeeded, failed } = await runSequentialSaves(filled, ({ row }) =>
      // The GraphQL requester maps `size` with vehicleSizeToAPISize, which expects the pt-BR label
      // ("Pequeno"/"Médio"/"Grande") — parseVehicleSize produces exactly that from the enum.
      createVehicle({
        licensePlate: formatLastValidPlate(row.licensePlate.trim()),
        size: parseVehicleSize(row.size as VehicleSize),
        speedLimitInKmh: row.speedLimitInKmh > 0 ? row.speedLimitInKmh : undefined,
      } as Vehicle)
    );
    // Created vehicles exist even when another row fails, so the event reflects reality.
    if (succeeded.length > 0) Analytics.track('BasicValueModalItemCreated', { fleetId, feature: 'vehicles', count: succeeded.length });
    if (failed.length > 0) {
      // Keep the form open for retry, dropping the rows that saved so retrying can't duplicate them.
      form.reset({ rows: rowsForRetry(rows, new Set(succeeded.map(({ rowIndex }) => rowIndex)), ROWS, () => ({ licensePlate: '', size: '', speedLimitInKmh: 0 })) });
      void form.trigger();
      return;
    }
    onSuccess(succeeded.length);
  };

  return (
    <DialogSetupGuideShell
      open
      onClose={onClose}
      title={t('entity_modal.vehicles.title')}
      description={t('entity_modal.vehicles.description')}
      secondaryAction={{ label: t('entity_modal.advanced_settings'), onClick: onAdvancedSettings }}
      primaryAction={{
        label: t('entity_modal.vehicles.submit'),
        onClick: form.handleSubmit(handleSubmit),
        disabled: !form.formState.isValid || isPending,
        loading: isPending,
        endIcon: <Icon name='check' size='sm' />,
        'data-testid': 'setup-guide-vehicles-submit',
      }}
    >
      <Form form={form} onSubmit={handleSubmit}>
        <div className='flex flex-col'>
          <Tag color='default' size='sm' label={t('entity_modal.vehicles.progress', { current: item.current, target: item.target })} />
          {Array.from({ length: ROWS }, (_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-size row list, never reordered
            <SetupGuideFormRow key={index} label={t('entity_modal.vehicles.row_label', { index: index + 1 })}>
              <div className='flex w-full flex-col gap-4'>
                <div className='flex flex-wrap items-start gap-4'>
                  <FormField control={form.control} name={`rows.${index}.licensePlate`}>
                    {({ field }) => (
                      <FormItem className='w-56'>
                        <FormLabel>{t('entity_modal.vehicles.plate_label')}</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            autoComplete='off'
                            onChange={(event) => field.onChange(event.target.value.toUpperCase())}
                            placeholder={t('entity_modal.vehicles.plate_placeholder')}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  </FormField>
                  <FormField control={form.control} name={`rows.${index}.size`}>
                    {({ field }) => (
                      <FormItem className='w-56'>
                        <FormLabel>{t('entity_modal.vehicles.size_label')}</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value}
                            onValueChange={(size) => {
                              field.onChange(size);
                              // Porte suggests the allowed speed; the slider stays adjustable after.
                              form.setValue(`rows.${index}.speedLimitInKmh`, DEFAULT_KMH[size as VehicleSize], { shouldValidate: true });
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t('entity_modal.vehicles.size_placeholder')} />
                            </SelectTrigger>
                            <SelectContent portal={false}>
                              {PORTE_ORDER.map((size) => (
                                <SelectItem key={size} value={size}>
                                  {parseVehicleSize(size)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  </FormField>
                </div>
                <FormField control={form.control} name={`rows.${index}.speedLimitInKmh`}>
                  {({ field }) => (
                    <FormItem className='w-full max-w-xl'>
                      <FormLabel>{t('entity_modal.vehicles.speed_label')}</FormLabel>
                      <FormControl>
                        <div className='[&>div]:mx-0'>
                          <Slider.Discrete
                            fromRight={false}
                            value={field.value}
                            min={0}
                            max={MAX_SPEED_LIMIT}
                            onChange={(values) => field.onChange(values[0] ?? 0)}
                            measuringUnit='km/h'
                            customSteps={Array.from({ length: 16 }, (_, step) => step * 10)}
                            customMarks={Array.from({ length: 16 }, (_, step) => step * 10)}
                            customCaptions={[10, 50, 100, 150]}
                            fixedSizeSegmentsSteps
                          />
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                </FormField>
              </div>
            </SetupGuideFormRow>
          ))}
        </div>
      </Form>
    </DialogSetupGuideShell>
  );
};

export default VehiclesForm;
